import React, { useState, useEffect } from 'react';
import PolicyDocuments from '../components/PolicyDocuments';
import './Pages.css';

const ApprovedCancellations = () => {
  const [cancellations, setCancellations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCancellation, setSelectedCancellation] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchApprovedCancellations();
  }, []);

  const fetchApprovedCancellations = async () => {
    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/cancellations/approved');
      const data = await response.json();

      if (data.success) {
        setCancellations(data.data);
      } else {
        setError('Failed to load approved cancellations');
      }
    } catch (err) {
      console.error('Error fetching cancellations:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleViewCancellation = (cancellation) => {
    setSelectedCancellation(cancellation);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCancellation(null);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading approved cancellations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Approved Cancellations</h1>
        <span className="badge-count success">{cancellations.length} Approved</span>
      </div>

      {error && (
        <div className="error-banner">
          {error}
        </div>
      )}

      <div className="table-container">
        {cancellations.length === 0 ? (
          <div className="empty-state">
            <p>No approved cancellations</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Policy Number</th>
                <th>Client Name</th>
                <th>Registration</th>
                <th>Cancellation Reason</th>
                <th>Requested By</th>
                <th>Date Requested</th>
                <th>Approved By</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cancellations.map((cancellation, index) => (
                <tr key={cancellation.Id}>
                  <td>{index + 1}</td>
                  <td className="policy-number">{cancellation.PolicyNumber || 'N/A'}</td>
                  <td className="client-name">{cancellation.ClientName || 'N/A'}</td>
                  <td>{cancellation.Registration || 'N/A'}</td>
                  <td className="cancellation-reason">
                    {cancellation.CancellationReason?.substring(0, 50)}
                    {cancellation.CancellationReason?.length > 50 ? '...' : ''}
                  </td>
                  <td>{cancellation.RequestedByName || cancellation.CancelledBy || 'N/A'}</td>
                  <td>{formatDate(cancellation.DateOfCancellation)}</td>
                  <td>{cancellation.ApprovedByName || cancellation.ApprovedBy || 'N/A'}</td>
                  <td>
                    <button 
                      className="action-icon-btn" 
                      title="View Details"
                      onClick={() => handleViewCancellation(cancellation)}
                    >
                      👁️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* View Cancellation Modal */}
      {showModal && selectedCancellation && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✅ Approved Cancellation</h2>
              <button className="close-modal-btn" onClick={handleCloseModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              {/* Policy Information */}
              <div className="detail-section">
                <h3>Policy Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Policy Number:</label>
                    <span>{selectedCancellation.PolicyNumber || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Registration:</label>
                    <span>{selectedCancellation.Registration || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Client Name:</label>
                    <span>{selectedCancellation.ClientName || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Client Email:</label>
                    <span>{selectedCancellation.ClientEmail || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Cancellation Details */}
              <div className="detail-section">
                <h3>Cancellation Details</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Cancellation Reason:</label>
                    <span>{selectedCancellation.CancellationReason || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Approval Info */}
              <div className="detail-section">
                <h3>Approval Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Requested By:</label>
                    <span>{selectedCancellation.RequestedByName || selectedCancellation.CancelledBy || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Date Requested:</label>
                    <span>{formatDate(selectedCancellation.DateOfCancellation)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Approved By:</label>
                    <span>{selectedCancellation.ApprovedByName || selectedCancellation.ApprovedBy || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className="status-badge active">Approved</span>
                  </div>
                </div>
              </div>

              <PolicyDocuments policy={selectedCancellation} />

              {/* Action Button */}
              <div className="modal-actions">
                <button 
                  className="action-button cancel-endorsement-btn"
                  onClick={handleCloseModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovedCancellations;
